import { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/api/repository/axios';
import type { FaqGroup, FaqGroupCode, FaqItem } from '@/api/repository/types/faq';
import { ListBody, ListFilterRow } from '@/components/common/organism/ListPageLayout';
import { FilterChip, FilterChipBar, filterTokens } from '@/components/common/filter';
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import PageState from '@/components/common/PageState';

/** 주소의 FAQ 칸 — `?tab=faq&faq=<id>`(그 질문을 펼쳐 연다), 분류 `fgroup` */
export const FAQ_PARAM = 'faq';
const GROUP_PARAM = 'fgroup';
const SEARCH_DELAY_MS = 250;

/**
 * 공지사항의 '자주 묻는 질문' 탭 본문 — 필터 줄(검색 칸·분류 칩)과 목록(질문 누르면 펼침).
 * `ListPageShell` 의 바로 아래 자식으로 놓이도록 조각(fragment)으로 돌려준다(UI 가이드 6.1 — 탭 줄만 고정, 필터 줄은 목록과 함께 올라감).
 * 회원 구분으로 거르지 않는다 — 대상이 정해진 질문은 "치과 회원" 같은 표시만 붙는다.
 */
const FaqList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const group = (searchParams.get(GROUP_PARAM) || undefined) as FaqGroupCode | undefined;
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [groups, setGroups] = useState<FaqGroup[]>([]);
    const [items, setItems] = useState<FaqItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [retry, setRetry] = useState(0);
    const [openIds, setOpenIds] = useState<Set<string>>(() => new Set(searchParams.get(FAQ_PARAM) ? [searchParams.get(FAQ_PARAM)!] : []));
    const scrolledRef = useRef(false);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(query), SEARCH_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setFailed(false);
        api.faq.list(group, debounced)
            .then((response) => {
                if (!alive) return;
                setGroups(response.result?.groups ?? []);
                setItems(response.result?.items ?? []);
            })
            .catch(() => alive && setFailed(true))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [group, debounced, retry]);

    // 링크로 들어온 질문은 한 번만 화면으로 옮긴다
    useEffect(() => {
        const target = searchParams.get(FAQ_PARAM);
        if (!target || scrolledRef.current || loading) return;
        const element = document.getElementById(`faq-${target}`);
        if (element) {
            scrolledRef.current = true;
            element.scrollIntoView({ block: 'center' });
        }
    }, [loading, searchParams]);

    const total = useMemo(() => groups.reduce((sum, g) => sum + g.count, 0), [groups]);

    const selectGroup = (code?: FaqGroupCode) => {
        const next = new URLSearchParams(searchParams);
        if (code) next.set(GROUP_PARAM, code); else next.delete(GROUP_PARAM);
        next.delete(FAQ_PARAM);
        setSearchParams(next, { replace: true });
    };

    const toggle = (id: string) => setOpenIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    return (
        <>
            <ListFilterRow>
                <SearchBox>
                    <SearchRoundedIcon aria-hidden fontSize="inherit" />
                    <SearchInput
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value.slice(0, 50))}
                        placeholder="궁금한 내용을 검색해 보세요"
                        aria-label="자주 묻는 질문 검색"
                        enterKeyHint="search"
                    />
                </SearchBox>
                <ChipRow>
                    <FilterChipBar ariaLabel="자주 묻는 질문 분류">
                        <FilterChip label={`전체${total ? ` ${total}` : ''}`} toggle selected={!group} onClick={() => selectGroup(undefined)} />
                        {groups.map((g) => (
                            <FilterChip key={g.code} label={g.label} toggle selected={group === g.code} onClick={() => selectGroup(group === g.code ? undefined : g.code)} />
                        ))}
                    </FilterChipBar>
                </ChipRow>
            </ListFilterRow>
            <ListBody>
                {loading && items.length === 0 ? (
                    <LoadingSpinner placement="inline" minHeight="200px" />
                ) : failed ? (
                    <PageState title="자주 묻는 질문을 불러오지 못했어요" error onRetry={() => setRetry((n) => n + 1)} />
                ) : items.length === 0 ? (
                    <PageState
                        title="찾는 질문이 없어요"
                        description="다른 낱말로 검색하거나 문의하기로 물어봐 주세요."
                        action={{ label: '문의하기', to: '/inquiry' }}
                    />
                ) : (
                    <FaqRows items={items} openIds={openIds} onToggle={toggle} />
                )}
            </ListBody>
        </>
    );
};

export default FaqList;

/** 질문 목록(누르면 펼침) — 데이터만 받는 부분(SSR 검사용으로 나눴다) */
export const FaqRows = ({ items, openIds, onToggle }: { items: FaqItem[]; openIds: Set<string>; onToggle: (id: string) => void }) => (
    <Rows data-faq-list="">
        {items.map((item) => {
            const open = openIds.has(item.id);
            return (
                <Row key={item.id} id={`faq-${item.id}`}>
                    <QuestionButton type="button" aria-expanded={open} aria-controls={`faq-answer-${item.id}`} onClick={() => onToggle(item.id)}>
                        <QuestionText>
                            <Meta>
                                <span>{item.groupLabel}</span>
                                {item.audience && <Audience>{item.audience}</Audience>}
                            </Meta>
                            <Question>{item.question}</Question>
                        </QuestionText>
                        <Chevron open={open}><ExpandMoreRoundedIcon fontSize="inherit" /></Chevron>
                    </QuestionButton>
                    {open && <Answer id={`faq-answer-${item.id}`}>{item.answer}</Answer>}
                </Row>
            );
        })}
    </Rows>
);

const SearchBox = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 44px;
    padding: 0 14px;
    border: 1px solid ${filterTokens.color.border};
    border-radius: 12px;
    background: ${filterTokens.color.surface};
    color: ${filterTokens.color.textMuted};
    font-size: 20px;
    box-sizing: border-box;

    &:focus-within {
        border-color: ${filterTokens.color.accent};
    }
`;

const SearchInput = styled.input`
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    /* 아이폰이 입력 때 확대하지 않게 16px */
    font-size: 16px;
    color: ${filterTokens.color.text};

    &::placeholder { color: ${filterTokens.color.textMuted}; }
`;

const ChipRow = styled.div`
    margin-top: 10px;
`;

const Rows = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const Row = styled.li`
    border-bottom: 1px solid ${filterTokens.color.divider};
`;

const QuestionButton = styled.button`
    appearance: none;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 64px;
    padding: 12px 0;
    border: none;
    background: none;
    text-align: left;
    font-family: inherit;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:focus-visible {
        outline: 2px solid ${filterTokens.color.accent};
        outline-offset: -2px;
    }
`;

const QuestionText = styled.span`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

/* 대상 표시가 있어도 없어도 줄 높이가 같게(18px) — 행 높이가 들쭉날쭉하지 않게 */
const Meta = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    height: 18px;
    font-size: 12px;
    line-height: 18px;
    color: ${filterTokens.color.textMuted};
`;

const Audience = styled.span`
    padding: 0 6px;
    line-height: 18px;
    border-radius: 4px;
    background: ${filterTokens.color.selectedBg};
    color: ${filterTokens.color.selectedText};
    font-weight: 600;
`;

const Question = styled.span`
    font-size: 15px;
    font-weight: 700;
    line-height: 1.45;
    color: ${filterTokens.color.text};
    word-break: keep-all;
    overflow-wrap: anywhere;
`;

const Chevron = styled.span<{ open: boolean }>`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    font-size: 24px;
    color: ${filterTokens.color.textMuted};
    transform: rotate(${({ open }) => (open ? 180 : 0)}deg);
    transition: transform 0.15s ease;

    svg { display: block; }
`;

const Answer = styled.div`
    margin: 0 0 16px;
    padding: 14px 16px;
    border-radius: 10px;
    background: #F5F8F7;
    font-size: 14px;
    line-height: 1.65;
    color: ${filterTokens.color.textSub};
    white-space: pre-wrap;
    word-break: keep-all;
    overflow-wrap: anywhere;
`;
